/**
 * The live workplace activity feed.
 *
 * Every item here maps to a record the server actually holds: a workplace event, a notification,
 * an assignment row or a department announcement. Nothing is invented, inferred or padded — if
 * the API returns nothing, the feed is empty and the UI says so. Copy is written in the voice of
 * the simulated workplace (TechCore Solutions); names come from the workplace overview and
 * scenario titles from the catalog, never from guesses.
 */
import type { Assignment, Notification, WorkplaceEvent } from '../api/types';
import type { IconName } from '../components/ui/Icon';

export type ActivityOrigin = 'event' | 'notification' | 'assignment' | 'announcement';
export type ActivityTone = 'accent' | 'ok' | 'warn' | 'neutral';

export interface ActivityItem {
  /** Stable id: origin plus the record id it was built from. */
  id: string;
  /** ISO timestamp of the underlying record. */
  at: string;
  title: string;
  detail?: string;
  icon: IconName;
  tone: ActivityTone;
  href?: string;
  origin: ActivityOrigin;
  /** Notifications only: still unread. */
  unread?: boolean;
}

export interface ActivityAnnouncement {
  id: string;
  title: string;
  body: string;
  authorId: string;
  authorName?: string;
  postedAt: string;
}

export interface ActivityInput {
  events?: WorkplaceEvent[];
  notifications?: Notification[];
  assignments?: Assignment[];
  announcements?: ActivityAnnouncement[];
}

export interface ActivityContext {
  /** The learner's manager, from the workplace overview. */
  managerName?: string;
  /** The learner's department, from the workplace overview. */
  departmentName?: string;
  /** Scenario id → title, from catalog.scenarios(). */
  scenarioTitles?: Record<string, string>;
}

const MAX_DETAIL = 150;

const humanize = (s: string) => s.replace(/[-_.]/g, ' ').replace(/\s+/g, ' ').trim();

function shorten(text: string | undefined, max = MAX_DETAIL): string | undefined {
  if (!text) return undefined;
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return undefined;
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

function field(detail: unknown, key: string): string | undefined {
  if (!detail || typeof detail !== 'object') return undefined;
  const v = (detail as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : undefined;
}

function numField(detail: unknown, key: string): number | undefined {
  if (!detail || typeof detail !== 'object') return undefined;
  const v = (detail as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : undefined;
}

function detailPairs(detail: unknown): string | undefined {
  if (!detail || typeof detail !== 'object') return undefined;
  const parts = Object.entries(detail as Record<string, unknown>)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .map(([k, v]) => `${humanize(k)}: ${String(v)}`);
  return parts.length ? shorten(parts.join(' · ')) : undefined;
}

function scenarioName(ctx: ActivityContext, id: string | undefined): string | undefined {
  if (!id) return undefined;
  return ctx.scenarioTitles?.[id] ?? humanize(id);
}

const manager = (ctx: ActivityContext) => ctx.managerName ?? 'Your manager';

function fromEvent(e: WorkplaceEvent, ctx: ActivityContext): ActivityItem {
  const id = `event:${e.id}`;
  const scenarioId = field(e.detail, 'scenarioId');
  const sessionId = field(e.detail, 'sessionId');
  const title = scenarioName(ctx, scenarioId);
  const sessionHref = scenarioId ? `#/scenario/${encodeURIComponent(scenarioId)}${sessionId ? `?session=${encodeURIComponent(sessionId)}` : ''}` : undefined;
  switch (e.type) {
    case 'scenario.started':
      return { id, at: e.at, origin: 'event', icon: 'play', tone: 'accent', href: sessionHref, title: `You picked up ${title ?? 'a scenario'}`, detail: ctx.departmentName ? `${ctx.departmentName} · simulated workplace task` : 'Simulated workplace task' };
    case 'scenario.completed': {
      const score = numField(e.detail, 'score');
      const reviewer = field(e.detail, 'manager');
      const bits = [score === undefined ? undefined : `Evaluated at ${Math.round(score * 100)}%`, reviewer ? `reviewed by ${reviewer}` : undefined].filter(Boolean);
      return { id, at: e.at, origin: 'event', icon: 'check', tone: 'ok', href: sessionHref, title: `You completed ${title ?? 'a scenario'}`, detail: bits.length ? bits.join(' · ') : undefined };
    }
    case 'assignment.created':
      return { id, at: e.at, origin: 'event', icon: 'clipboard', tone: 'accent', href: '#/assignments', title: `New assignment from ${manager(ctx)}`, detail: title };
    default:
      return { id, at: e.at, origin: 'event', icon: 'info', tone: 'neutral', title: humanize(e.type), detail: detailPairs(e.detail) };
  }
}

const NOTIFICATION_ICON: Record<string, IconName> = {
  assignment: 'clipboard',
  'manager-feedback': 'chat',
  message: 'mail',
  announcement: 'mail',
};

export function notificationIcon(kind: string): IconName {
  return NOTIFICATION_ICON[kind] ?? 'bell';
}

function fromNotification(n: Notification): ActivityItem {
  return {
    id: `notification:${n.id}`,
    at: n.createdAt,
    origin: 'notification',
    icon: notificationIcon(n.kind),
    tone: n.kind === 'manager-feedback' ? 'ok' : 'accent',
    href: n.link,
    title: n.title,
    detail: shorten(n.body),
    unread: !n.readAt,
  };
}

function fromAssignment(a: Assignment, ctx: ActivityContext): ActivityItem | undefined {
  // 'assigned' and 'in-progress' already arrive as an event or a notification; only the later
  // states of the row add something the feed does not otherwise hold.
  if (a.status === 'submitted') {
    return { id: `assignment:${a.id}`, at: a.updatedAt, origin: 'assignment', icon: 'clipboard', tone: 'accent', href: '#/assignments', title: `You submitted ${a.title} for review`, detail: shorten(a.brief) };
  }
  if (a.status === 'reviewed') {
    return { id: `assignment:${a.id}`, at: a.updatedAt, origin: 'assignment', icon: 'check', tone: 'ok', href: '#/assignments', title: `${manager(ctx)} reviewed ${a.title}`, detail: shorten(a.feedback) };
  }
  return undefined;
}

function fromAnnouncement(a: ActivityAnnouncement): ActivityItem {
  return {
    id: `announcement:${a.id}`,
    at: a.postedAt,
    origin: 'announcement',
    icon: 'mail',
    tone: 'neutral',
    title: `${a.authorName ?? humanize(a.authorId)} posted “${a.title}”`,
    detail: shorten(a.body),
  };
}

/** Two records describe the same moment when they are the same fact seconds apart. */
function withinSeconds(a: string, b: string, seconds: number): boolean {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return false;
  return Math.abs(ta - tb) <= seconds * 1000;
}

/**
 * Merge every workplace record into one feed, newest first. The server writes an
 * `assignment.created` event and an `assignment` notification for the same hand-off, and a
 * reviewed assignment arrives with its manager-feedback notification, so the duplicate is
 * dropped: one fact, one line.
 */
export function buildActivity(input: ActivityInput, ctx: ActivityContext = {}, limit = 40): ActivityItem[] {
  const notifications = (input.notifications ?? []).map(fromNotification);
  const assignmentNotices = (input.notifications ?? []).filter((n) => n.kind === 'assignment');
  const events = (input.events ?? [])
    .filter((e) => !(e.type === 'assignment.created' && assignmentNotices.some((n) => withinSeconds(n.createdAt, e.at, 120))))
    .map((e) => fromEvent(e, ctx));
  const feedbackNotices = (input.notifications ?? []).filter((n) => n.kind === 'manager-feedback');
  const assignments = (input.assignments ?? [])
    // A reviewed assignment arrives with its manager-feedback notification: keep the manager's words.
    .filter((a) => !(a.status === 'reviewed' && feedbackNotices.some((n) => withinSeconds(n.createdAt, a.updatedAt, 120))))
    .map((a) => fromAssignment(a, ctx))
    .filter((x): x is ActivityItem => Boolean(x));
  const announcements = (input.announcements ?? []).map(fromAnnouncement);
  const all = [...events, ...notifications, ...assignments, ...announcements];
  const seen = new Set<string>();
  return all
    .filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)))
    .sort((a, b) => (a.at === b.at ? a.id.localeCompare(b.id) : b.at.localeCompare(a.at)))
    .slice(0, limit);
}

export interface ActivityDay {
  /** Local calendar day, YYYY-MM-DD. */
  key: string;
  label: string;
  items: ActivityItem[];
}

const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Group the feed by the day each record was written, newest day first. */
export function groupActivityByDay(items: ActivityItem[], now: Date = new Date()): ActivityDay[] {
  const today = dayKey(now);
  const yesterdayDate = new Date(now.getTime());
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = dayKey(yesterdayDate);
  const days: ActivityDay[] = [];
  for (const item of items) {
    const d = new Date(item.at);
    if (Number.isNaN(d.getTime())) continue;
    const key = dayKey(d);
    let group = days.find((g) => g.key === key);
    if (!group) {
      const label = key === today ? 'Today' : key === yesterday ? 'Yesterday' : d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
      group = { key, label, items: [] };
      days.push(group);
    }
    group.items.push(item);
  }
  return days.sort((a, b) => b.key.localeCompare(a.key));
}

/** "just now", "6 minutes ago", "3 hours ago", "yesterday", then a date. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const diff = Math.max(0, now.getTime() - t);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** The stamp next to a feed that refreshes itself. */
export function refreshStamp(lastLoaded: number, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - lastLoaded) / 1000));
  if (seconds < 45) return 'Updated just now';
  const minutes = Math.round(seconds / 60);
  return minutes <= 1 ? 'Updated a minute ago' : `Updated ${minutes} minutes ago`;
}
